"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MenuIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SearchButton } from "../SearchBar";

import { AiButton } from "../AiAssistant";

import { MenuSelectorDialog } from "../MenuSelector";
import { cn } from "@/lib/utils";

import { useLocationMenus } from "@/hooks/useLocationMenus";

const allItems = [
  { id: "search", component: SearchButton },
  { id: "ai", component: AiButton },
  {
    id: "change-menu",
    component: MenuSelectorDialog,
  },
];

export default function Menu({ showLabel = false }: { showLabel?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const { hasMultipleMenus } = useLocationMenus();
  const hasAiAssistant = false;

  const toggleMenu = () => setIsOpen(!isOpen);

  const enabledMenuItems = [
    "search",
    hasMultipleMenus ? "change-menu" : null,
    hasAiAssistant ? "ai" : null,
  ].filter((item) => item !== null);

  if (enabledMenuItems.length === 0) return null; // If no items are enabled, return null

  if (enabledMenuItems.length === 1 && enabledMenuItems[0] === "search")
    return null; // Search is always visible

  const items = allItems.filter((item) => enabledMenuItems.includes(item.id));

  return (
    <div className="flex items-center justify-center z-50">
      <TooltipProvider delayDuration={300}>
        <div className="relative">
          {/* Main Menu Button */}
          <motion.div
            layout
            className="relative z-10"
            initial={false}
            animate={{
              scale: isOpen ? 0.9 : 1,
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
          >
            <Button
              onClick={toggleMenu}
              size="icon"
              className={cn(
                "h-14 min-w-14 w-auto rounded-lg bg-background hover:bg-muted hover:text-muted-foreground text-foreground shadow-lg transition-colors",
                showLabel ? "px-4 flex gap-3" : ""
              )}
            >
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                }}
              >
                {isOpen ? (
                  <X className="h-6 w-6 text-foreground" />
                ) : (
                  <MenuIcon className="h-6 w-6 text-foreground" />
                )}
              </motion.div>

              {showLabel && !isOpen && <span>Menu</span>}
            </Button>
          </motion.div>

          {/* Menu Items */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 flex flex-col-reverse gap-3 "
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.2,
                  staggerChildren: 0.1,
                  delayChildren: 0.1,
                }}
              >
                {items.map((item, index) => {
                  if (!item.component) return null;
                  return (
                    <motion.div
                      key={item.id}
                      initial={{
                        opacity: 0,
                        y: 20,
                        scale: 0.8,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                      }}
                      exit={{
                        opacity: 0,
                        y: 20,
                        scale: 0.8,
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 25,
                        delay: index * 0.05,
                      }}
                    >
                      <div className="relative">
                        <item.component
                          showLabel
                          onClose={() => {
                            setIsOpen(false);
                          }}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Background Overlay */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className="fixed inset-0 bg-black/20 backdrop-blur-sm -z-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setIsOpen(false)}
              />
            )}
          </AnimatePresence>
        </div>
      </TooltipProvider>
    </div>
  );
}
